import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, Check, Play, Plus, X, Minus, Sparkles } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { Reveal } from "@/components/site/Reveal";
import { ContactForm } from "@/components/site/ContactForm";
import { StillsMarquee } from "@/components/site/StillsMarquee";
import { PrimeVideoReel } from "@/components/site/PrimeVideoReel";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { primeServices, type PrimeAccent, type PrimeService } from "@/content/prime-services";
import { trackCta, trackConversion } from "@/lib/analytics";
import { requestPrimeQuote } from "@/lib/prime-quote.functions";
import { cn } from "@/lib/utils";

// ---------- Accent token helpers ----------
const accentText: Record<PrimeAccent, string> = {
  mint: "text-mint",
  peach: "text-peach",
  butter: "text-butter",
};
const accentBg: Record<PrimeAccent, string> = {
  mint: "bg-mint",
  peach: "bg-peach",
  butter: "bg-butter",
};
const accentBorderHover: Record<PrimeAccent, string> = {
  mint: "hover:border-mint/40",
  peach: "hover:border-peach/40",
  butter: "hover:border-butter/40",
};
const accentGlow: Record<PrimeAccent, string> = {
  mint: "from-mint/25 via-mint/5 to-transparent",
  peach: "from-peach/25 via-peach/5 to-transparent",
  butter: "from-butter/25 via-butter/5 to-transparent",
};
const accentVar: Record<PrimeAccent, string> = {
  mint: "var(--mint)",
  peach: "var(--peach)",
  butter: "var(--butter)",
};
const aspectClass: Record<PrimeService["hero"]["aspect"], string> = {
  anamorphic: "aspect-[2.39/1]",
  wide: "aspect-[16/9]",
  portrait: "aspect-[4/5] md:aspect-[3/2]",
  square: "aspect-[5/4] md:aspect-[4/3]",
};

// ---------- Cinematic Hero ----------
function CinematicHero({ service }: { service: PrimeService }) {
  const a = service.accent;
  return (
    <header className="relative overflow-hidden pt-28">
      {/* Atmospheric gradient backdrop */}
      <div
        aria-hidden
        className={cn("pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b", accentGlow[a])}
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-px bg-white/10" />

      <div className="container-wide pb-10 md:pb-16">
        <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          <Link to="/prime" className="hover:text-foreground">Prime</Link>
          <span>·</span>
          <span className={cn("font-mono", accentText[a])}>{service.number}</span>
          <span>·</span>
          <span>{service.shortTitle}</span>
        </div>

        <Reveal>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl lg:text-[88px]">
            {service.shortTitle}
          </h1>
        </Reveal>
        <Reveal delay={80}>
          <p className="mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-2xl">
            {service.kicker}
          </p>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-5 max-w-3xl text-pretty text-base text-muted-foreground md:text-lg">
            {service.intro}
          </p>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              to="/kontakt"
              variant="mint"
              arrow
              trackId="book_strategy_meeting"
              trackProps={{ location: `prime_service_${service.slug}_hero` }}
            >
              Boka strategimöte
            </Button>
            <Button to="/case" variant="outline">Se case</Button>
          </div>
        </Reveal>

        {/* Hero media frame — cinematic, poster-first, gradient fallback */}
        <Reveal delay={260}>
          <div
            className={cn(
              "relative mt-14 overflow-hidden rounded-3xl border border-white/10 bg-surface",
              aspectClass[service.hero.aspect],
            )}
            style={{
              boxShadow: `0 30px 80px -20px color-mix(in oklab, ${accentVar[a]} 28%, transparent)`,
            }}
          >
            <HeroVisual service={service} />
            {service.hero.aspect === "anamorphic" && (
              <>
                <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[6%] bg-black" />
                <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-[6%] bg-black" />
              </>
            )}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-5 md:p-7">
              <div className="text-xs uppercase tracking-[0.18em] text-white/70">
                {service.slug === "poddar" ? `Studiopodd · ${service.signature.year}` : `Signaturverk · ${service.signature.year}`}
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/70">
                {service.signature.platform}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </header>
  );
}

// ---------- Hero visual (gradient + discipline-specific motif) ----------
function HeroVisual({ service }: { service: PrimeService }) {
  const a = service.accent;
  const flavor = service.hero.flavor;

  if (service.hero.videoSrc) {
    return (
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
        src={service.hero.videoSrc}
      />
    );
  }

  if (service.signature.videoId) {
    return (
      <VideoEmbed
        id={service.signature.videoId}
        title={service.signature.title}
        priority="high"
        className="absolute inset-0 h-full w-full rounded-none"
      />
    );
  }

  // Procedural cinematic poster — no media file needed
  return (
    <div className="absolute inset-0">
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(120% 80% at 20% 20%, color-mix(in oklab, ${accentVar[a]} 38%, transparent) 0%, transparent 60%), radial-gradient(80% 60% at 80% 80%, color-mix(in oklab, ${accentVar[a]} 22%, transparent) 0%, transparent 70%), linear-gradient(180deg, oklch(0.18 0 0), oklch(0.10 0 0))`,
        }}
      />
      {flavor === "studio" && <StudioMosaic accent={a} />}
      {flavor === "podcast" && <PodcastWave accent={a} />}
      {flavor === "post" && <BeforeAfterPoster accent={a} />}
      {flavor === "director" && <DirectorBoard accent={a} />}
      {flavor === "broadcast" && <BroadcastBars accent={a} />}
      {flavor === "cinema" && <CinemaGrain accent={a} />}
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_60%,rgba(0,0,0,0.55)_100%)]" />
    </div>
  );
}

function StudioMosaic({ accent }: { accent: PrimeAccent }) {
  return (
    <div className="absolute inset-[6%] grid grid-cols-3 grid-rows-2 gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "rounded-md border border-white/10 bg-white/[0.04] backdrop-blur-[2px]",
            i === 0 && "col-span-2 row-span-2",
          )}
        >
          <div className="flex h-full w-full items-end justify-between p-2 text-[10px] font-mono text-white/60">
            <span>CAM {i + 1}</span>
            {i === 0 && (
              <span className={cn("rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-black", accentBg[accent])}>
                ● ON AIR
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PodcastWave({ accent }: { accent: PrimeAccent }) {
  const bars = Array.from({ length: 56 }, (_, i) => {
    const t = i / 56;
    const h = 18 + Math.abs(Math.sin(t * 12)) * 70 + Math.abs(Math.cos(t * 6)) * 30;
    return Math.min(120, Math.round(h));
  });
  return (
    <div className="absolute inset-x-[6%] bottom-[14%] flex items-end gap-[3px]">
      {bars.map((h, i) => (
        <div
          key={i}
          className={cn("flex-1 rounded-sm", accentBg[accent])}
          style={{ height: `${h}px`, opacity: 0.35 + (i % 7) * 0.08 }}
        />
      ))}
    </div>
  );
}

function BeforeAfterPoster({ accent }: { accent: PrimeAccent }) {
  return (
    <>
      <div
        className="absolute inset-y-0 left-0 w-1/2"
        style={{ background: "linear-gradient(135deg, oklch(0.22 0 0), oklch(0.14 0 0))", filter: "saturate(0.4) brightness(0.85)" }}
      />
      <div
        className="absolute inset-y-0 right-0 w-1/2"
        style={{ background: `linear-gradient(135deg, color-mix(in oklab, ${accentVar[accent]} 28%, oklch(0.14 0 0)), oklch(0.10 0 0))` }}
      />
      <div className={cn("absolute inset-y-0 left-1/2 w-px -translate-x-1/2", accentBg[accent])} />
      <div className="absolute left-6 top-6 text-[10px] font-mono uppercase tracking-widest text-white/70">Före</div>
      <div className="absolute right-6 top-6 text-[10px] font-mono uppercase tracking-widest text-white/70">Efter</div>
    </>
  );
}

function DirectorBoard({ accent }: { accent: PrimeAccent }) {
  return (
    <div className="absolute inset-[8%] grid grid-cols-3 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-md border border-white/10 bg-white/[0.05] p-2">
          <div className="mb-2 h-2/3 rounded-sm bg-white/[0.06]" />
          <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-white/60">
            <span>SC {String(i + 1).padStart(2, "0")}</span>
            <span className={accentText[accent]}>●</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function BroadcastBars({ accent }: { accent: PrimeAccent }) {
  return (
    <div className="absolute inset-x-[6%] bottom-[18%] flex h-6 overflow-hidden rounded-sm">
      {["#7a7a7a", "#c8c800", "#00c8c8", "#00c800", "#c800c8", "#c80000", "#0000c8"].map((c, i) => (
        <div key={i} className="flex-1" style={{ background: c, opacity: 0.55 }} />
      ))}
      <div className={cn("ml-2 w-2", accentBg[accent])} />
    </div>
  );
}

function CinemaGrain({ accent }: { accent: PrimeAccent }) {
  return (
    <div
      className="absolute inset-0 mix-blend-overlay opacity-40"
      style={{
        backgroundImage: `radial-gradient(circle at 30% 40%, color-mix(in oklab, ${accentVar[accent]} 18%, transparent) 0%, transparent 35%)`,
      }}
    />
  );
}

// ---------- Sticky Proof Bar ----------
function StickyProofBar({ service }: { service: PrimeService }) {
  return (
    <div className="sticky top-20 z-30 border-y border-white/10 bg-background/85 backdrop-blur-md">
      <div className="container-wide flex flex-wrap items-center gap-x-8 gap-y-2 py-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
        <span>Sedan 2018</span>
        <span aria-hidden>·</span>
        <span>240+ produktioner</span>
        <span aria-hidden className="hidden md:inline">·</span>
        <span className={cn("hidden md:inline", accentText[service.accent])}>{service.priceFrom.amount} {service.priceFrom.unit}</span>
      </div>
    </div>
  );
}

// ---------- Signature Reel ----------
function SignatureReel({ service }: { service: PrimeService }) {
  const a = service.accent;
  const { videoId, spotifyShowUrl } = service.signature;
  const spotifyEmbedSrc = spotifyShowUrl
    ? spotifyShowUrl.replace("open.spotify.com/", "open.spotify.com/embed/")
    : null;
  return (
    <Section className="bg-surface/40">
      <div className="grid gap-10 md:grid-cols-[1fr_1.2fr] md:gap-14">
        <div>
          <EyebrowHeading
            eyebrow="Signaturverk"
            title={<>Ett verk i fokus — {service.signature.title}.</>}
          />
          <dl className="mt-8 space-y-3 text-sm">
            {[
              ["Kund", service.signature.client],
              ["Roll", service.signature.role],
              ["År", service.signature.year],
              ["Plattform", service.signature.platform],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-4 border-b border-white/5 pb-2">
                <dt className="w-24 text-xs uppercase tracking-[0.18em] text-muted-foreground">{k}</dt>
                <dd className="text-foreground/90">{v}</dd>
              </div>
            ))}
          </dl>
          {spotifyEmbedSrc && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-black/40">
              <iframe
                src={spotifyEmbedSrc}
                width="100%"
                height="232"
                frameBorder={0}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title={`Spotify-podd: ${service.signature.title}`}
              />
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              to="/case"
              variant="outline"
              arrow
              trackId="prime_signature_reel_play"
              trackProps={{ location: `prime_${service.slug}_signature`, label: service.signature.title }}
            >
              Se hela caset
            </Button>
          </div>
        </div>
        <Reveal direction="up" delay={80}>
          {videoId ? (
            <div
              className={cn(
                "relative w-full overflow-hidden rounded-3xl border border-white/10 bg-black",
                aspectClass[service.hero.aspect],
              )}
            >
              <VideoEmbed
                id={videoId}
                title={`Signaturverk: ${service.signature.title}`}
                className="absolute inset-0 h-full w-full rounded-none"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() =>
                trackCta("prime_signature_reel_play", {
                  location: `prime_${service.slug}_signature`,
                  label: service.signature.title,
                })
              }
              className={cn(
                "group relative block w-full overflow-hidden rounded-3xl border border-white/10 bg-surface text-left",
                aspectClass[service.hero.aspect],
              )}
              aria-label={`Spela signaturverk: ${service.signature.title}`}
            >
              <HeroVisual service={service} />
              <span className="absolute inset-0 flex items-center justify-center">
                <span
                  className={cn(
                    "flex h-20 w-20 items-center justify-center rounded-full transition-transform duration-500 group-hover:scale-110",
                    accentBg[a],
                  )}
                  style={{
                    boxShadow: `0 20px 50px -10px color-mix(in oklab, ${accentVar[a]} 55%, transparent)`,
                  }}
                >
                  <Play className="h-7 w-7 fill-black text-black" />
                </span>
              </span>
            </button>
          )}
        </Reveal>
      </div>
    </Section>
  );
}

// ---------- Capability Grid ----------
function CapabilityGrid({ service }: { service: PrimeService }) {
  return (
    <Section>
      <EyebrowHeading eyebrow="Vad vi gör" title={`Inom ${service.shortTitle.toLowerCase()}`} />
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {service.capabilities.map((cap, idx) => (
          <Reveal key={cap.title} delay={idx * 80}>
            <article
              className={cn(
                "group flex h-full flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-all duration-300 hover:-translate-y-1",
                accentBorderHover[service.accent],
              )}
            >
              <Sparkles className={cn("h-5 w-5", accentText[service.accent])} />
              <h3 className="mt-5 text-xl font-semibold leading-tight">{cap.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{cap.body}</p>
              <div className="mt-auto pt-6">
                <div className={cn("inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1 text-[11px] font-mono uppercase tracking-widest", accentText[service.accent])}>
                  <span className={cn("h-1.5 w-1.5 rounded-full", accentBg[service.accent])} />
                  {cap.proof}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ---------- Proof Strip ----------
function ProofStrip({ service }: { service: PrimeService }) {
  return (
    <Section className="bg-surface/40">
      <div className="grid gap-10 md:grid-cols-[1.1fr_1fr] md:gap-16">
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">Vi har levererat för</div>
          <div className="grid grid-cols-3 gap-x-6 gap-y-5 sm:grid-cols-3 md:grid-cols-3">
            {service.proofLogos.map((logo) => (
              <div
                key={logo}
                className="flex h-12 items-center justify-center rounded-lg border border-white/10 bg-black/30 text-sm font-semibold text-foreground/70"
              >
                {logo}
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:grid-cols-1">
          {service.proofMetrics.map((m, i) => (
            <Reveal key={m.label} delay={i * 80}>
              <div
                className="rounded-2xl border border-white/10 bg-surface p-6"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${accentVar[service.accent]} 12%, transparent)`,
                }}
              >
                <div className={cn("text-4xl font-semibold leading-none md:text-5xl", accentText[service.accent])}>
                  {m.value}
                </div>
                <div className="mt-3 text-sm text-muted-foreground">{m.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ---------- Fit / Not for you ----------
function FitCheck({ service }: { service: PrimeService }) {
  return (
    <Section>
      <div className="grid gap-10 md:grid-cols-2 md:gap-16">
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">När det passar</div>
          <h2 className="text-balance text-3xl font-semibold leading-tight md:text-4xl">
            Det här är för er som…
          </h2>
          <ul className="mt-8 space-y-4">
            {service.whenItFits.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base text-foreground/90">
                <Check className={cn("mt-1 h-4 w-4 shrink-0", accentText[service.accent])} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">Det här är inte för er</div>
          <h2 className="text-balance text-3xl font-semibold leading-tight md:text-4xl">
            Vi säger hellre nej tidigt.
          </h2>
          <ul className="mt-8 space-y-4">
            {service.notForYou.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base text-muted-foreground">
                <X className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

// ---------- Deliverables + Pricing ----------
function DeliverablesPricing({ service }: { service: PrimeService }) {
  const hasPackages = !!service.pricingPackages?.length;
  return (
    <Section className="bg-surface/40">
      <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:gap-16">
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">Konkret leverans</div>
          <h2 className="text-balance text-3xl font-semibold leading-tight md:text-4xl">
            Vad ni får i handen.
          </h2>
          <ul className="mt-8 space-y-4">
            {service.deliverables.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base text-foreground/90">
                <Check className={cn("mt-1 h-4 w-4 shrink-0", accentText[service.accent])} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <Reveal direction="up">
          <aside
            className="rounded-3xl border border-white/10 bg-surface p-7"
            style={{
              backgroundImage: `linear-gradient(160deg, color-mix(in oklab, ${accentVar[service.accent]} 10%, transparent), transparent 60%)`,
            }}
          >
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {hasPackages ? "Upplägg & priser" : "Indikativt prisspann"}
            </div>
            {hasPackages ? (
              <div className="mt-5 space-y-4">
                {service.pricingPackages!.map((p) => (
                  <div key={p.name} className="rounded-2xl border border-white/10 bg-background/40 p-4">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-sm font-semibold text-foreground">{p.name}</div>
                      <div className={cn("text-lg font-semibold", accentText[service.accent])}>{p.price}</div>
                    </div>
                    <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{p.unit}</div>
                    <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                      {p.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                          <Check className={cn("mt-0.5 h-3 w-3 shrink-0", accentText[service.accent])} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p className="text-xs leading-relaxed text-muted-foreground">{service.priceFrom.note}</p>
              </div>
            ) : (
              <>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className={cn("text-4xl font-semibold leading-none md:text-5xl", accentText[service.accent])}>
                    {service.priceFrom.amount}
                  </div>
                  <div className="text-sm text-muted-foreground">{service.priceFrom.unit}</div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{service.priceFrom.note}</p>
              </>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                to="/priser"
                variant="mint"
                arrow
                trackId="prime_pricing_inline_click"
                trackProps={{ location: `prime_${service.slug}_pricing` }}
              >
                Se hela prisbilden
              </Button>
              <Button to="/kontakt" variant="outline">
                Få exakt offert
              </Button>
            </div>
          </aside>
        </Reveal>
      </div>
    </Section>
  );
}

// ---------- Podcast "Lyssna" (Spotify embed under hero) ----------
function PodcastListen({ service }: { service: PrimeService }) {
  const { spotifyShowUrl, title } = service.signature;
  const spotifyEmbedSrc = spotifyShowUrl
    ? spotifyShowUrl.replace("open.spotify.com/", "open.spotify.com/embed/")
    : null;
  if (!spotifyEmbedSrc) return null;
  return (
    <Section className="bg-surface/40">
      <EyebrowHeading eyebrow="Lyssna" title={<>Hör podden vi byggde — {title}.</>} />
      <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-black/40">
        <iframe
          src={spotifyEmbedSrc}
          width="100%"
          height="352"
          frameBorder={0}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          title={`Spotify-podd: ${title}`}
        />
      </div>
    </Section>
  );
}

// ---------- Process Timeline ----------
function ProcessTimeline({ service }: { service: PrimeService }) {
  return (
    <Section>
      <EyebrowHeading
        eyebrow="Så funkar Prime"
        title="En förutsägbar rytm — ni vet alltid vad som händer och när."
      />
      <ol className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
        {service.process.map((p, idx) => (
          <Reveal key={p.n} delay={idx * 60}>
            <li
              className={cn(
                "relative h-full rounded-3xl border border-white/10 bg-surface p-6 transition-colors",
                accentBorderHover[service.accent],
              )}
            >
              <div className={cn("text-3xl font-semibold", accentText[service.accent])}>{p.n}</div>
              <h3 className="mt-3 text-lg font-semibold leading-tight">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </Section>
  );
}

// ---------- FAQ ----------
function FAQ({ service }: { service: PrimeService }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <Section className="bg-surface/40">
      <EyebrowHeading eyebrow="Vanliga frågor" title="Snabba svar innan ni bokar." />
      <ul className="mt-12 mx-auto max-w-3xl divide-y divide-white/10 border-y border-white/10">
        {service.faq.map((f, i) => {
          const isOpen = open === i;
          return (
            <li key={f.q}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex w-full items-center justify-between gap-6 py-5 text-left"
                aria-expanded={isOpen}
              >
                <span className="text-base font-medium text-foreground md:text-lg">{f.q}</span>
                {isOpen ? (
                  <Minus className={cn("h-4 w-4 shrink-0", accentText[service.accent])} />
                ) : (
                  <Plus className={cn("h-4 w-4 shrink-0", accentText[service.accent])} />
                )}
              </button>
              {isOpen && (
                <div className="pb-5 pr-10 text-sm leading-relaxed text-muted-foreground md:text-base">{f.a}</div>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// ---------- Quick Quote Form ----------
function QuickQuoteForm({ service }: { service: PrimeService }) {
  const [email, setEmail] = useState("");
  const [brief, setBrief] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const submit = useServerFn(requestPrimeQuote);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setState("sending");
    try {
      const { getAttribSnapshot } = await import("@/lib/analytics/track");
      const attrib = getAttribSnapshot();
      await submit({
        data: {
          service_slug: service.slug,
          email,
          brief: brief || null,
          source_path: typeof window !== "undefined" ? window.location.pathname : null,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          session_id: attrib.session_id,
          visitor_id: attrib.visitor_id,
        },
      });
      trackConversion({
        conversion_type: "lead_magnet_download",
        location: `prime_${service.slug}_quick_quote`,
        label: service.shortTitle,
      });
      trackCta("prime_quick_quote_submit", {
        location: `prime_${service.slug}_quick_quote`,
        label: service.shortTitle,
      });
      setState("done");
    } catch {
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <div
        className="rounded-3xl border border-white/10 bg-surface p-7 text-center"
        style={{
          backgroundImage: `linear-gradient(160deg, color-mix(in oklab, ${accentVar[service.accent]} 14%, transparent), transparent 60%)`,
        }}
      >
        <div className={cn("text-sm font-semibold uppercase tracking-[0.18em]", accentText[service.accent])}>
          Tack — vi hörs inom 24 h
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Vi återkommer med en första riktning för {service.shortTitle.toLowerCase()} och tider för ett möte.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-3xl border border-white/10 bg-surface p-7"
      style={{
        backgroundImage: `linear-gradient(160deg, color-mix(in oklab, ${accentVar[service.accent]} 10%, transparent), transparent 60%)`,
      }}
    >
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Få offert via mail</div>
      <h3 className="mt-2 text-2xl font-semibold leading-tight md:text-3xl">
        Skicka in på 30 sek — vi svarar inom 24 h.
      </h3>
      <div className="mt-6 grid gap-3">
        <input
          type="email"
          required
          placeholder="din@mail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-lg border border-white/15 bg-black/30 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-white/40 focus:outline-none"
        />
        <textarea
          placeholder={`Kort om er ${service.shortTitle.toLowerCase()}-idé (frivilligt)`}
          rows={3}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          className="w-full rounded-lg border border-white/15 bg-black/30 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-white/40 focus:outline-none"
        />
        <Button type="submit" variant="mint" arrow disabled={state === "sending"}>
          {state === "sending" ? "Skickar…" : "Skicka förfrågan"}
        </Button>
        {state === "error" && (
          <p className="text-xs text-peach">Något gick fel — testa igen eller maila hey@fenny.se.</p>
        )}
      </div>
    </form>
  );
}

// ---------- Closing CTA + Related ----------
function ClosingAndRelated({ service }: { service: PrimeService }) {
  const related = service.related
    .map((slug) => primeServices.find((s) => s.slug === slug))
    .filter(Boolean) as PrimeService[];

  return (
    <Section>
      <div className="grid gap-10 md:grid-cols-[1.1fr_1fr] md:gap-14">
        <div>
          <EyebrowHeading
            eyebrow="Nästa steg"
            title="Boka 30 min — eller skicka en kort förfrågan."
            lede="Två vägar in. Båda landar hos samma team."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              to="/kontakt"
              variant="mint"
              arrow
              trackId="book_strategy_meeting"
              trackProps={{ location: `prime_${service.slug}_closing` }}
            >
              Boka strategimöte
            </Button>
            <Button to="/case" variant="outline">Se case</Button>
          </div>
        </div>
        <QuickQuoteForm service={service} />
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <EyebrowHeading
            eyebrow="Närliggande Prime"
            title="Kombinera flera — samma team, samma projektledare."
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {related.map((s) => (
              <Link
                key={s.slug}
                to="/prime/$slug"
                params={{ slug: s.slug }}
                onClick={() =>
                  trackCta("prime_related_click", {
                    location: `prime_${service.slug}_related`,
                    label: s.shortTitle,
                  })
                }
                className={cn(
                  "group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors",
                  accentBorderHover[s.accent],
                )}
              >
                <div className={cn("font-mono text-xs", accentText[s.accent])}>{s.number}</div>
                <h3 className="mt-3 text-xl font-semibold leading-tight">{s.shortTitle}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground line-clamp-3">{s.intro}</p>
                <span className={cn("mt-6 inline-flex items-center gap-1.5 text-sm transition-transform group-hover:translate-x-0.5", accentText[s.accent])}>
                  Läs mer <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------- Main exported page ----------
export function PrimeServicePage({ service }: { service: PrimeService }) {
  return (
    <div>
      <CinematicHero service={service} />
      <StickyProofBar service={service} />
      {service.slug !== "poddar" && <SignatureReel service={service} />}
      {service.slug === "poddar" && <PodcastListen service={service} />}
      <CapabilityGrid service={service} />
      <PrimeVideoReel service={service} />
      {service.stills && service.stills.length >= 3 && (
        <StillsMarquee
          images={service.stills}
          accent={service.accent}
          title={
            service.slug === "poddar"
              ? "Från studion"
              : `Från produktionerna · ${service.shortTitle}`
          }
          lede={
            service.slug === "poddar"
              ? "Stillbilder från inspelningar i Fennys poddstudio."
              : "Ett axplock från färska set — ljus, miljöer och människorna framför linsen."
          }
        />
      )}
      <ProofStrip service={service} />
      <FitCheck service={service} />
      <DeliverablesPricing service={service} />
      <ProcessTimeline service={service} />
      <FAQ service={service} />
      <ClosingAndRelated service={service} />
      <ContactForm location={`prime_${service.slug}`} defaultIntent="prime" />
    </div>
  );
}
